<template>
  <a-drawer
    :visible="visible"
    title="📂 이벤트 자료실"
    placement="right"
    :width="600"
    :destroyOnClose="true"
    @close="handleClose"
  >
    <!-- 설명 -->
    <div class="drawer-description">
      <a-alert type="info" show-icon style="margin-bottom: 16px">
        <template #message>
          <div>
            <p style="margin: 0 0 8px 0;">이 이벤트와 관련된 이미지, 문서를 관리합니다.</p>
            <p style="margin: 0;">업로드된 파일 URL을 복사하여 페이지 디자인에 활용하세요.</p>
          </div>
        </template>
      </a-alert>
    </div>

    <!-- 파일 업로드 섹션 -->
    <div class="upload-section">
      <div class="section-header">
        <h4>파일 업로드</h4>
      </div>

      <a-form layout="vertical">
        <!-- 파일 선택 -->
        <a-form-item label="파일 선택">
          <a-upload
            v-model:file-list="fileList"
            :multiple="true"
            :before-upload="beforeUpload"
            :show-upload-list="true"
            @remove="handleRemove"
          >
            <a-button>
              <template #icon><i class="fa fa-upload"></i></template>
              파일 선택
            </a-button>
          </a-upload>
          <div class="hint-text">
            * 최대 10MB, 이미지/문서/텍스트 파일만 업로드 가능
          </div>
        </a-form-item>

        <!-- 폴더 경로 (선택) -->
        <a-form-item label="폴더 경로 (선택)">
          <a-input
            v-model:value="folderPath"
            placeholder="예) banner/main 또는 thumbnails (입력하지 않으면 events/ 에 저장)"
            allow-clear
          />
          <div class="hint-text" v-if="folderPath">
            저장 경로: <strong>events/{{ folderPath }}/</strong>
          </div>
        </a-form-item>

        <!-- 리소스 타입 (자동 감지, 수정 가능) -->
        <a-form-item label="리소스 분류" v-if="fileList.length > 0">
          <a-select v-model:value="resourceType" size="small">
            <a-select-option value="image">이미지</a-select-option>
            <a-select-option value="document">문서</a-select-option>
            <a-select-option value="other">기타</a-select-option>
          </a-select>
        </a-form-item>

        <!-- 설명 (선택) -->
        <a-form-item label="설명 (선택)" v-if="fileList.length > 0">
          <a-textarea
            v-model:value="description"
            :rows="2"
            placeholder="파일에 대한 간단한 설명"
          />
        </a-form-item>

        <!-- 업로드 버튼 -->
        <a-form-item v-if="fileList.length > 0">
          <div style="display: flex; gap: 8px;">
            <a-button @click="clearUpload">취소</a-button>
            <a-button
              type="primary"
              :loading="uploading"
              @click="handleUpload"
              class="yellow-button"
            >
              <template #icon><i class="fa fa-upload"></i></template>
              업로드
            </a-button>
          </div>
        </a-form-item>
      </a-form>
    </div>

    <!-- 구분선 -->
    <a-divider />

    <!-- 파일 목록 섹션 -->
    <div class="files-section">
      <div class="section-header">
        <h4>업로드된 파일</h4>
        <a-button @click="refreshFiles" :loading="loading" size="small">
          <template #icon><i class="fa fa-sync-alt"></i></template>
          새로고침
        </a-button>
      </div>

      <!-- 경로 네비게이션 (Breadcrumb) -->
      <div class="path-navigation">
        <a-breadcrumb>
          <a-breadcrumb-item>
            <a @click="handleBreadcrumbClick('events/')">
              <i class="fa fa-home"></i> 홈
            </a>
          </a-breadcrumb-item>
          <a-breadcrumb-item
            v-for="(crumb, index) in pathBreadcrumbs.slice(1)"
            :key="index"
          >
            <a
              v-if="!crumb.isLast"
              @click="handleBreadcrumbClick(crumb.path)"
            >
              {{ crumb.name }}
            </a>
            <span v-else>{{ crumb.name }}</span>
          </a-breadcrumb-item>
        </a-breadcrumb>
      </div>

      <!-- 필터 -->
      <div class="filter-bar">
        <a-radio-group v-model:value="currentFilter" @change="handleFilterChange" size="small">
          <a-radio-button value="all">전체</a-radio-button>
          <a-radio-button value="image">이미지</a-radio-button>
          <a-radio-button value="document">문서</a-radio-button>
          <a-radio-button value="other">기타</a-radio-button>
        </a-radio-group>
      </div>

      <!-- 파일 리스트 -->
      <ResourceList
        :files="filteredFiles"
        :loading="loading"
        @preview="handlePreview"
        @copy-url="handleCopyUrl"
        @folder-click="handleFolderClick"
      />
    </div>

    <!-- 하단 통계 -->
    <div class="drawer-footer-stats">
      <span>전체 {{ files.length }}개</span>
      <span>{{ totalSize }}</span>
    </div>

    <!-- 미리보기 모달 -->
    <PreviewModal
      v-if="showPreviewModal"
      v-model:visible="showPreviewModal"
      :file="selectedFile"
    />
  </a-drawer>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import ResourceList from './resources/ResourceList.vue';
import PreviewModal from '@/views/Storage/components/PreviewModal.vue';

export default defineComponent({
  name: 'EventResourceDrawer',

  components: {
    ResourceList,
    PreviewModal
  },

  props: {
    visible: {
      type: Boolean,
      required: true
    },
    eventId: {
      type: String,
      default: null
    },
    api: {
      type: Object,
      required: true
    }
  },

  emits: ['update:visible'],

  setup(props, { emit }) {
    const loading = ref(false);
    const uploading = ref(false);
    const files = ref([]);
    const currentFilter = ref('all');
    const showPreviewModal = ref(false);
    const selectedFile = ref(null);

    // 경로 탐색 관련 상태
    const currentPath = ref('events/');

    // 업로드 관련 상태
    const fileList = ref([]);
    const folderPath = ref('');
    const resourceType = ref('image');
    const description = ref('');

    // 파일 확장자로 리소스 타입 판단
    const getResourceType = (fileName) => {
      if (!fileName) return 'other';

      const ext = fileName.split('.').pop()?.toLowerCase();

      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
        return 'image';
      } else if (['pdf', 'doc', 'docx', 'xls', 'xlsx'].includes(ext)) {
        return 'document';
      } else {
        return 'other';
      }
    };

    // 경로를 breadcrumb 형태로 변환
    const pathBreadcrumbs = computed(() => {
      const parts = currentPath.value.split('/').filter(Boolean);
      const breadcrumbs = [];

      let accumulatedPath = '';
      parts.forEach((part, index) => {
        accumulatedPath += part + '/';
        breadcrumbs.push({
          name: part,
          path: accumulatedPath,
          isLast: index === parts.length - 1
        });
      });

      return breadcrumbs;
    });

    // 필터링된 파일 목록
    const filteredFiles = computed(() => {
      if (currentFilter.value === 'all') {
        return files.value;
      }

      return files.value.filter(file => {
        const resourceType = getResourceType(file.name);
        return resourceType === currentFilter.value;
      });
    });

    // 전체 파일 크기 계산
    const totalSize = computed(() => {
      const bytes = files.value.reduce((sum, file) => sum + (file.size || 0), 0);
      return formatFileSize(bytes);
    });

    // 파일 크기 포맷
    const formatFileSize = (bytes) => {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    // 파일 목록 로드
    const loadFiles = async () => {
      loading.value = true;

      try {
        const response = await props.api.getEventResources(props.eventId, 'all', currentPath.value);

        if (response.success) {
          files.value = response.data.items || [];
          console.log(`${files.value.length}개의 항목을 불러왔습니다. (경로: ${currentPath.value})`);
        } else {
          message.error(response.error || '목록을 불러오는데 실패했습니다.');
          files.value = [];
        }
      } catch (error) {
        console.error('목록 로드 오류:', error);
        message.error('목록을 불러오는데 실패했습니다.');
        files.value = [];
      } finally {
        loading.value = false;
      }
    };

    // 폴더 클릭 핸들러
    const handleFolderClick = (folder) => {
      currentPath.value = folder.path;
      loadFiles();
    };

    // 경로 breadcrumb 클릭 핸들러
    const handleBreadcrumbClick = (path) => {
      currentPath.value = path;
      loadFiles();
    };

    // 새로고침
    const refreshFiles = () => {
      loadFiles();
    };

    // 파일 업로드 전 검증
    const beforeUpload = (file) => {
      // 파일 유형 검사
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'application/json'
      ];

      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'json'];

      // 확장자와 MIME 타입 검사
      const isAllowedType = allowedTypes.includes(file.type) || allowedExts.includes(fileExt);
      if (!isAllowedType) {
        message.error(`${file.name} 파일 형식은 지원되지 않습니다.`);
        return false;
      }

      // 파일 크기 검사 (10MB로 제한)
      const isLessThan10M = file.size / 1024 / 1024 < 10;
      if (!isLessThan10M) {
        message.error(`${file.name} 파일이 10MB를 초과합니다.`);
        return false;
      }

      // 리소스 타입 자동 감지
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt)) {
        resourceType.value = 'image';
      } else if (['pdf', 'doc', 'docx', 'xls', 'xlsx'].includes(fileExt)) {
        resourceType.value = 'document';
      } else {
        resourceType.value = 'other';
      }

      // 수동 업로드를 위해 false 반환
      return false;
    };

    // 파일 제거
    const handleRemove = (file) => {
      const index = fileList.value.indexOf(file);
      if (index > -1) {
        fileList.value.splice(index, 1);
      }
    };

    // 업로드 취소
    const clearUpload = () => {
      fileList.value = [];
      folderPath.value = '';
      resourceType.value = 'image';
      description.value = '';
    };

    // 업로드 핸들러
    const handleUpload = async () => {
      if (fileList.value.length === 0) {
        message.warning('파일을 선택해주세요.');
        return;
      }

      if (!props.api) {
        message.error('API 인스턴스를 찾을 수 없습니다.');
        return;
      }

      uploading.value = true;

      try {
        // 파일 객체 추출
        const uploadFiles = fileList.value.map(item => {
          if (item.originFileObj instanceof File) {
            return item.originFileObj;
          }
          return item;
        }).filter(file => file instanceof File);

        if (uploadFiles.length === 0) {
          message.error('유효한 파일이 없습니다.');
          uploading.value = false;
          return;
        }

        // 메타데이터 구성
        const metadata = {
          resourceType: resourceType.value,
          description: description.value || ''
        };

        // 업로드 API 호출 (폴더 경로 포함)
        const response = await props.api.uploadEventResource(props.eventId, uploadFiles, metadata, folderPath.value);

        if (response.success) {
          message.success('파일이 성공적으로 업로드되었습니다.');
          clearUpload();
          loadFiles();
        } else {
          throw new Error(response.error || '업로드 실패');
        }
      } catch (error) {
        console.error('파일 업로드 실패:', error);
        message.error(`업로드 실패: ${error.message || '알 수 없는 오류'}`);
      } finally {
        uploading.value = false;
      }
    };

    // 미리보기
    const handlePreview = (file) => {
      selectedFile.value = file;
      showPreviewModal.value = true;
    };

    // URL 복사
    const handleCopyUrl = async (file) => {
      try {
        const url = file.url;

        // 클립보드에 복사
        await navigator.clipboard.writeText(url);

        // 성공 메시지
        message.success('URL이 클립보드에 복사되었습니다.');

        // URL 미리보기 토스트
        message.info({
          content: `${file.name}`,
          duration: 2
        });
      } catch (error) {
        console.error('URL 복사 실패:', error);
        message.error('URL 복사에 실패했습니다.');
      }
    };


    // 필터 변경
    const handleFilterChange = () => {
      console.log('필터 변경:', currentFilter.value);
    };

    // Drawer 닫기
    const handleClose = () => {
      emit('update:visible', false);
    };

    // visible이 true가 되면 파일 목록 로드
    watch(() => props.visible, (newVal) => {
      if (newVal) {
        loadFiles();
      }
    });

    // 마운트 시 파일 목록 로드
    onMounted(() => {
      if (props.visible) {
        loadFiles();
      }
    });

    return {
      loading,
      uploading,
      files,
      currentFilter,
      showPreviewModal,
      selectedFile,
      filteredFiles,
      totalSize,
      // 경로 탐색
      currentPath,
      pathBreadcrumbs,
      handleFolderClick,
      handleBreadcrumbClick,
      // 업로드 관련
      fileList,
      folderPath,
      resourceType,
      description,
      beforeUpload,
      handleRemove,
      clearUpload,
      handleUpload,
      // 파일 관리
      loadFiles,
      refreshFiles,
      handlePreview,
      handleCopyUrl,
      handleFilterChange,
      handleClose
    };
  }
});
</script>

<style lang="scss" scoped>
.drawer-description {
  margin-bottom: 16px;

  :deep(.ant-alert-message) {
    p {
      font-size: 13px;
      line-height: 1.5;
    }
  }
}

.upload-section,
.files-section {
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }
}

.hint-text {
  font-size: 12px;
  color: #6b7280;
  margin-top: 8px;
}

.path-navigation {
  margin-bottom: 12px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;

  :deep(.ant-breadcrumb) {
    font-size: 13px;

    a {
      color: #666;
      transition: color 0.2s;

      &:hover {
        color: #000;
      }
    }

    .ant-breadcrumb-separator {
      color: #d9d9d9;
    }
  }
}

.filter-bar {
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;

  :deep(.ant-radio-group) {
    display: flex;
    width: 100%;

    .ant-radio-button-wrapper {
      flex: 1;
      text-align: center;
    }
  }
}

.drawer-footer-stats {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #666;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;

  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

:deep(.ant-drawer-body) {
  padding-bottom: 60px; // 하단 통계 영역 공간 확보
}

// 폼 스타일
:deep(.ant-form-item-label) {
  padding: 0 0 8px;

  label {
    color: #000;
    font-size: 13px;
    height: auto;
    font-weight: 500;
  }
}

:deep(.ant-upload) {
  .ant-btn {
    height: 36px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;

    &:hover {
      border-color: #000;
      color: #000;
    }
  }
}

:deep(.ant-select) {
  width: 100%;

  .ant-select-selector {
    height: 36px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    display: flex;
    align-items: center;

    &:hover,
    &:focus {
      border-color: #000;
      box-shadow: none;
    }
  }

  .ant-select-selection-item {
    line-height: normal;
  }
}

:deep(.ant-input-textarea) {
  .ant-input {
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;

    &:hover,
    &:focus {
      border-color: #000;
      box-shadow: none;
    }
  }
}
</style>
